import { Component, Event, EventEmitter, h, Method, Prop } from '@stencil/core';
import { FormRenderProps, FormValues } from '../../stencil-form/declarations';
import InputField from '../../stencil-form/InputField';
import { Unsubscribe } from '@stencil/redux';
import { store } from '../../../redux/store';
import { BillingAddress, FieldRequirement, FormConfig, ShippingAddress, StepItem, Translations } from '../../../redux/types';

import SelectField from '../../stencil-form/SelectField';
import { fetchCustomer, markStepAsLoaded } from '../../../redux/actions';
import { billingKey, trans } from '../../../utils/trans';
import { getCountriesOptions, getCountryFromLocale } from '../../../utils/utils';
import { RootState } from '../../../redux/reducers';

@Component({
  tag: 'rentware-billing-step',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareBillingStep {
  @Event({ eventName: 'rentwareSubmitBilling' }) submitBilling: EventEmitter;
  @Prop({ mutable: true }) formConfig: FormConfig;
  @Prop({ mutable: true }) translations: Translations;
  @Prop({ mutable: true }) customer: any;
  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) defaultLanguage: string;
  @Prop() billingStepValues: any;
  @Prop() onFormValid;
  @Prop() currentStep: StepItem;
  @Prop() errors;

  unsubscribe!: Unsubscribe;
  fetchCustomer!: typeof fetchCustomer;
  markStepAsLoaded!: typeof markStepAsLoaded;
  formRef: any;
  templateRef;

  @Method()
  async goToFirstError() {
    this.goToError();
  }

  @Method()
  async saveData(saveFunction: (values) => void) {
    const values = await this.formRef.submitForm();
    saveFunction(values);
  }

  @Method()
  async saveDataWithoutValidation(saveFunction: (values) => void) {
    const values = await this.formRef.submitFormWithoutValidation();
    saveFunction(values);
  }

  @Method()
  async validate(onSuccess: (values: any) => void, onFail: (values: any) => void) {
    const values = await this.formRef.submitForm();
    const isValid = await this.formRef.isFormValid();
    if (isValid && onSuccess) {
      onSuccess(values);
      return true;
    } else {
      if (!!onFail) onFail(values);
      return false;
    }
  }

  componentDidRender() {
    this.goToError();
  }

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        formConfig: state.system.formConfig,
        translations: state.system.translations,
        customer: state.app.customer,
        locale: state.app.locale,
        defaultLanguage: state.system.defaultLanguage,
      };
    });

    store.mapDispatchToProps(this, {
      fetchCustomer,
      markStepAsLoaded,
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  componentDidLoad() {
    this.fetchCustomer();
    this.markStepAsLoaded(this.currentStep.id);
  }

  goToError() {
    this.templateRef?.goToFirstError();
  }

  get initialValues() {
    if (this.billingStepValues) {
      return this.billingStepValues;
    }

    let initialValues: any = {
      country: getCountryFromLocale(this.defaultLanguage).toUpperCase(),
      shippingAddressSameAsAddress: this.formConfig.shippingAddressSameAsAddress,
      hideShipping: true,
    };

    if (this.customer) {
      initialValues = {
        ...this.customer.customer,
        ...this.customer.address,
        ...initialValues,
        phoneNumber: this.customer.customer.phone,
      };

      if (this.customer.customer && this.customer.customer.shipping) {
        Object.entries(this.customer.customer.shipping).forEach(([key, value]) => {
          initialValues['shipping.' + key] = value;
        });
      }

      if (this.customer.address && this.customer.address.shipping) {
        Object.entries(this.customer.address.shipping).forEach(([key, value]) => {
          initialValues['shipping.' + key] = value;
        });
      }
    }

    return initialValues;
  }

  render() {
    // means not loaded yet
    if (!this.customer) {
      return null;
    }

    return (
      <rentware-checkout-billing-template
        initialValues={this.initialValues}
        locale={this.locale}
        translations={this.translations}
        submitBilling={this.submitBilling}
        renderAddressFields={renderAddressFields}
        countriesOptions={getCountriesOptions(this.locale, this.translations)}
        formConfig={this.formConfig}
        billingFormRef={e => (this.formRef = e)}
        onFormValid={this.onFormValid}
        ref={el => (this.templateRef = el)}
        currentStep={this.currentStep}
        errors={this.errors}
      />
    );
  }
}

function renderAddressFields(
  namePrefix: string = '',
  address: BillingAddress | ShippingAddress,
  props: FormRenderProps<FormValues>,
  countryOptions = [],
  translations: Translations,
  locale: string,
) {
  const sorted = ['firstName', 'lastName', 'email', 'phone', '__SPACER__', 'street', 'addressTwo', 'city', 'zip', 'country', '__SPACER__', 'company', 'taxId'];

  return sorted.map(name => {
    if (name === '__SPACER__') {
      return <div class="spacerOne" />;
    }

    const opt = address[name];
    // not all fields exists for both address and shippingAddress
    if (!opt) {
      return null;
    }

    const fieldClass = 'InputField ' + name;

    if (opt === FieldRequirement.Hide) {
      return null;
    }

    let fieldName = mapName(name);
    if (namePrefix) {
      fieldName = `${namePrefix}.${fieldName}`;
    }

    let label = trans(billingKey(mapNameForTrans(name)), locale, translations);

    const validate: any = {};
    if (opt === FieldRequirement.Required) {
      validate.required = true;
    } else {
      label += ' (optional)'; // to replace by trans
    }

    if (name === 'country') {
      return (
        <div class={fieldClass}>
          <rentware-spacer size="M" />
          <SelectField //
            {...props}
            name={fieldName}
            label={label}
            options={countryOptions}
            validate={validate}
            noEmptyValue={true}
            scrollToClick={true}
            searchable={true}
          />
        </div>
      );
    }

    const inputType = name === 'email' ? 'email' : 'text';

    return (
      <div class={fieldClass}>
        <rentware-spacer size="M" />
        <InputField {...props} type={inputType} name={fieldName} label={label} validate={validate} />
      </div>
    );
  });
}

function mapName(name: string): string {
  const mapping = {
    zip: 'zipCode',
    phone: 'phoneNumber',
  };

  return mapping[name] ?? name;
}

function mapNameForTrans(name: string): string {
  const mapping = {
    lastName: 'surName',
    addressTwo: 'addressSecond',
  };

  return mapping[name] ?? name;
}
